/**
 * GPS / location metadata stripping (A3).
 *
 * Order matters and is the whole point: the bytes that get hashed and queued are
 * the sanitized bytes, and the camera's original is discarded. Re-encoding through
 * a canvas drops EXIF entirely (including GPS), which is the reliable browser-side
 * route — no EXIF library is added.
 */

/** EXIF markers we refuse to carry over even if a caller supplies a raw buffer. */
const GPS_IFD_TAG = 0x8825;

/**
 * Re-encode a source frame to a JPEG blob with no metadata.
 * Canvas output carries no EXIF, so GPS and device serials do not survive.
 */
export function stripViaCanvas(source, { maxEdge = 2048, quality = 0.85 } = {}) {
  return new Promise((resolve, reject) => {
    const sw = source.videoWidth || source.naturalWidth || source.width;
    const sh = source.videoHeight || source.naturalHeight || source.height;
    if (!sw || !sh) {
      reject(new Error('source has no dimensions'));
      return;
    }
    const scale = Math.min(1, maxEdge / Math.max(sw, sh));
    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.round(sw * scale));
    canvas.height = Math.max(1, Math.round(sh * scale));
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      reject(new Error('2d context unavailable'));
      return;
    }
    ctx.drawImage(source, 0, 0, canvas.width, canvas.height);
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error('toBlob returned null'))),
      'image/jpeg',
      quality,
    );
  });
}

/**
 * True when a JPEG buffer still contains a GPS IFD pointer. Used by tests and as a
 * belt-and-braces assertion before queueing raw bytes.
 */
export function bufferHasGpsIfd(buffer) {
  const view = new DataView(buffer);
  if (view.byteLength < 4) return false;
  if (view.getUint16(0, false) !== 0xffd8) return false; // not a JPEG
  let offset = 2;
  while (offset + 4 <= view.byteLength) {
    const marker = view.getUint16(offset, false);
    if ((marker & 0xff00) !== 0xff00) break;
    const size = view.getUint16(offset + 2, false);
    if (marker === 0xffe1) {
      // APP1 / EXIF: look for the GPS IFD tag inside the TIFF header.
      const tiff = offset + 4 + 6;
      if (tiff + 8 > view.byteLength) return false;
      const little = view.getUint16(tiff, false) === 0x4949;
      const ifd0 = tiff + view.getUint32(tiff + 4, little);
      if (ifd0 + 2 > view.byteLength) return false;
      const entries = view.getUint16(ifd0, little);
      for (let i = 0; i < entries; i += 1) {
        const entry = ifd0 + 2 + i * 12;
        if (entry + 2 > view.byteLength) break;
        if (view.getUint16(entry, little) === GPS_IFD_TAG) return true;
      }
    }
    if (marker === 0xffda) break; // start of scan; no more metadata
    offset += 2 + size;
  }
  return false;
}
